import React, { useEffect, useRef } from "react";
import Rellax from "rellax";
import PortableText from "./portableText";
import styles from "./intro.module.css";
import { cn } from "../lib/helpers";
import useToggleExpand from "../lib/useToggleExpand";
import { responsiveMicro, responsiveTitle2, bodyTextBlock } from "./typography.module.css";
import shieldVideo from "../videos/Capacity_Header_BG_new.mp4";
import { useMediaQuery } from "react-responsive";
import { mediaMaxSmall } from "../lib/mediaQueries";
import shieldIcon from "../images/shield-blank-red.svg";

const Intro = (props) => {
  const {
    title,
    body,
    buttonCopy,
    introBackgroundVideo,
    enableMobileBackgroundVideo,
    introBackgroundVideoMobile,
  } = props;
  const isMobile = useMediaQuery(mediaMaxSmall);

  // Toggle-gated mobile override: only swaps to the mobile-specific file
  // when Ellerey has both switched on "Use Mobile-Specific Background
  // Video" in Studio AND actually uploaded one for it. By default (toggle
  // off, or on with nothing uploaded yet), every viewport keeps using
  // introBackgroundVideo — same behavior as before this field existed.
  const useMobileVideo =
    isMobile && enableMobileBackgroundVideo && introBackgroundVideoMobile?.asset?.url;

  const videoSrc = useMobileVideo
    ? introBackgroundVideoMobile.asset.url
    : introBackgroundVideo
    ? introBackgroundVideo.asset.url
    : shieldVideo;

  const introTextRef = useRef(null);
  // Targets .videoWrapper (added 2026-09-15 alongside the CSS bottom-fade),
  // not the <video> tag itself — so the video and its .videoFade sibling
  // move together as one unit under Rellax's transform, instead of the
  // fade drifting out of alignment with the video during scroll.
  const videoWrapperRef = useRef(null);

  useEffect(() => {
    const rellaxOptions = {
      center: false,
      wrapper: null,
      round: true,
      vertical: true,
      horizontal: false,
    };
    const introTextRellax = new Rellax(introTextRef.current, {
      ...rellaxOptions,
      speed: isMobile ? 1 : 3,
    });
    const videoRellax = new Rellax(videoWrapperRef.current, {
      ...rellaxOptions,
      speed: -3,
    });
    const rellaxInstances = [introTextRellax, videoRellax];
    return () => {
      //reset for page changes
      rellaxInstances.forEach((instance) => {
        instance.destroy();
      });
    };
  });

  //body text toggle
  const toggleButtonRef = React.createRef();
  const bodyTextWrapper = React.createRef();
  const bodyTextInner = React.createRef();

  useToggleExpand(toggleButtonRef, bodyTextWrapper, bodyTextInner);

  return (
    <div className={styles.root}>
      <div className={styles.introWrapper}>
        {/* .videoWrapper carries the position/height that used to live on
            .video directly, and is what Rellax's parallax now moves as a
            unit — .video fills it (height:100%) and .videoFade sits on
            top, bottom:0, so both the video and its fade always move and
            resize together. */}
        <div className={styles.videoWrapper} ref={videoWrapperRef}>
          {/* key={videoSrc} forces a clean remount (and a real <video> reload)
              if the viewport crosses the mobile breakpoint while the page is
              open, instead of leaving a stale <source> in place — cheap
              insurance for a swap that's otherwise rare mid-session. */}
          <video className={styles.video} autoPlay muted loop key={videoSrc}>
            <source src={videoSrc} type="video/mp4" />
          </video>
          <div className={styles.videoFade} />
        </div>
        <div className={styles.textWrapper} ref={introTextRef}>
          <div className={styles.text}>
            <h2 className={responsiveTitle2}>{title}</h2>
            <button className={cn(responsiveMicro, styles.toggleButton)} ref={toggleButtonRef}>
              {buttonCopy}
            </button>
            {body && (
              <div ref={bodyTextWrapper} className={styles.bodyTextWrapper}>
                <div ref={bodyTextInner} className={cn(bodyTextBlock, styles.bodyTextInner)}>
                  <PortableText blocks={body} />
                </div>
              </div>
            )}
            <div className={styles.logoAccent}>
              <img className={styles.shieldIcon} src={shieldIcon} alt="Contact Button" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Intro;
